/**
 * DealDetailPanel — Slide-over panel showing complete deal intelligence.
 * Opens from right side on desktop, full-width on mobile.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import * as api from "@/api/client";
import type { DealData } from "@/api/client";
import type { Conversation } from "@/api/types";
import DealStageBadge from "@/components/shared/DealStageBadge";
import DSRProgressCard from "@/components/cards/DSRProgressCard";
import ThreatOpportunityCard from "@/components/cards/ThreatOpportunityCard";
import GuidedChips from "@/components/chat/GuidedChips";
import type { PromptChip } from "@/schemas/deals";

interface DealDetailPanelProps {
  dealId: string;
  onClose: () => void;
  onFollowUp?: (message: string) => void;
  onOpenConversation?: (convo: Conversation) => void;
  onUpdateDeal?: (updated: DealData) => void;
}

export default function DealDetailPanel({
  dealId,
  onClose,
  onFollowUp,
  onOpenConversation,
  onUpdateDeal,
}: DealDetailPanelProps) {
  const [deal, setDeal] = useState<DealData | null>(null);
  const [dsr, setDsr] = useState<Record<string, unknown> | null>(null);
  const [convos, setConvos] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Load all data on open
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.getDeal(dealId),
      api.getDealDSR(dealId),
      api.getDealConversations(dealId),
    ]).then(([d, dsr, c]) => {
      if (cancelled) return;
      setDeal(d);
      setDsr(dsr);
      setConvos(c);
      setNotes(d?.notes || "");
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [dealId]);

  // Save notes on blur
  const handleNotesBlur = useCallback(async () => {
    if (!deal || notes === (deal.notes || "")) return;
    setSavingNotes(true);
    const updated = await api.updateDeal(deal.id, { notes });
    if (updated) {
      setDeal(updated);
      onUpdateDeal?.(updated);
    }
    setSavingNotes(false);
  }, [deal, notes, onUpdateDeal]);

  // Status actions
  const doStatusUpdate = async (status: string) => {
    if (!deal) return;
    const updated = await api.updateDeal(deal.id, { status } as Record<string, string>);
    if (updated) {
      setDeal(updated);
      onUpdateDeal?.(updated);
    }
  };

  // Derive chips from DSR gaps
  const gapChips: PromptChip[] = [];
  if (dsr) {
    const pir = (dsr.pir_status as Record<string, string>) || {};
    const completeness = (dsr.completeness_score as number) || 0;
    if (pir.tax_analysis !== "complete") {
      gapChips.push({ label: "Check taxes", message: "What would the taxes be if I bought this property?", category: "discovery" });
    }
    if (pir.site_screening !== "complete") {
      gapChips.push({ label: "Screen the site", message: "Screen this site for zoning, flood zone, and environmental issues", category: "discovery" });
    }
    if (pir.market_analysis !== "complete") {
      gapChips.push({ label: "Market snapshot", message: "What's the market like in this area?", category: "analysis" });
    }
    if (pir.owner_research !== "complete") {
      gapChips.push({ label: "Research owner", message: "Who owns this property and what else do they own?", category: "analysis" });
    }
    if (completeness >= 60) {
      gapChips.push({ label: "Synthesize", message: "Give me your overall assessment of this deal", category: "synthesis" });
    }
  }

  // Timeline entries
  const timeline: Array<{ label: string; date: string }> = [];
  if (deal?.created_at) timeline.push({ label: "Created", date: deal.created_at });
  if (dsr?.last_tool_run) timeline.push({ label: "Last analysis", date: dsr.last_tool_run as string });
  if (deal?.updated_at && deal.updated_at !== deal.created_at) timeline.push({ label: "Last updated", date: deal.updated_at });

  const displayName = deal?.address || deal?.title || deal?.parcel_id || "Deal";
  const canPause = deal && ["screening", "active", "diligence"].includes(deal.status);
  const canResume = deal?.status === "paused";
  const canClose = deal && !["closed_won", "closed_lost"].includes(deal.status);

  return (
    <>
      <div className="deal-panel-backdrop" onClick={onClose} />
      <div className="deal-panel" ref={panelRef}>
        {loading ? (
          <div className="deal-panel-loading">
            <span className="spinner" /> Loading deal...
          </div>
        ) : !deal ? (
          <div className="deal-panel-loading">Deal not found</div>
        ) : (
          <>
            {/* Header */}
            <div className="deal-panel-header">
              <div className="deal-panel-header-left">
                <span className="deal-panel-address">{displayName}</span>
                <DealStageBadge stage={deal.status} />
              </div>
              <button className="deal-panel-close" onClick={onClose} aria-label="Close panel">
                &times;
              </button>
            </div>

            {/* Scrollable body */}
            <div className="deal-panel-body">
              {/* Quick Actions */}
              <div className="deal-panel-actions">
                {canPause && (
                  <button className="deal-panel-action-btn" onClick={() => doStatusUpdate("paused")}>
                    Pause
                  </button>
                )}
                {canResume && (
                  <button className="deal-panel-action-btn" onClick={() => doStatusUpdate("active")}>
                    Resume
                  </button>
                )}
                {canClose && (
                  <>
                    <button className="deal-panel-action-btn deal-panel-action-btn--success" onClick={() => doStatusUpdate("closed_won")}>
                      Close Won
                    </button>
                    <button className="deal-panel-action-btn deal-panel-action-btn--danger" onClick={() => doStatusUpdate("closed_lost")}>
                      Close Lost
                    </button>
                  </>
                )}
              </div>

              {/* Completeness */}
              {dsr && (
                <div className="deal-panel-section">
                  <DSRProgressCard
                    completeness={(dsr.completeness_score as number) || 0}
                    pirStatus={(dsr.pir_status as Record<string, string>) || {}}
                  />
                </div>
              )}

              {/* Key Metrics */}
              {dsr?.ground_truth != null && Object.keys(dsr.ground_truth as Record<string, unknown>).length > 0 && (() => {
                const gt = dsr.ground_truth as Record<string, unknown>;
                return (
                  <div className="deal-panel-section">
                    <div className="deal-panel-section-title">Key Metrics</div>
                    <div className="deal-panel-metrics">
                      {renderMetric("Assessed Value", formatCurrency(gt.assessed_value as number | undefined))}
                      {renderMetric("Tax Impact", formatCurrency(gt.investor_tax as number | undefined))}
                      {renderMetric("Risk Score", gt.risk_score != null ? `${String(gt.risk_score)}/100` : "—")}
                      {renderMetric("Market $/SF", formatCurrency(gt.market_price_per_sf as number | undefined))}
                    </div>
                  </div>
                );
              })()}

              {/* Threats & Opportunities */}
              {dsr && ((dsr.threats as string[])?.length > 0 || (dsr.opportunities as string[])?.length > 0) && (
                <div className="deal-panel-section">
                  <ThreatOpportunityCard
                    data={{
                      threats: (dsr.threats as string[]) || [],
                      opportunities: (dsr.opportunities as string[]) || [],
                      gaps: (dsr.gaps as string[]) || [],
                      gap_questions: [],
                    }}
                    onFollowUp={onFollowUp}
                  />
                </div>
              )}

              {/* Gap Action Chips */}
              {gapChips.length > 0 && onFollowUp && (
                <div className="deal-panel-section">
                  <div className="deal-panel-section-title">Next Steps</div>
                  <GuidedChips chips={gapChips.slice(0, 4)} onChipClick={onFollowUp} />
                </div>
              )}

              {/* Notes */}
              <div className="deal-panel-section">
                <div className="deal-panel-section-title">
                  Notes
                  {savingNotes && <span className="deal-panel-saving">Saving...</span>}
                </div>
                <textarea
                  className="deal-panel-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={handleNotesBlur}
                  placeholder="Add notes about this deal..."
                  rows={3}
                />
              </div>

              {/* Linked Conversations */}
              {convos.length > 0 && (
                <div className="deal-panel-section">
                  <div className="deal-panel-section-title">Conversations</div>
                  <div className="deal-panel-convos">
                    {convos.map((c) => (
                      <button
                        key={c.id}
                        className="deal-panel-convo"
                        onClick={() => {
                          onOpenConversation?.(c);
                          onClose();
                        }}
                      >
                        <span className="deal-panel-convo-title">{c.title}</span>
                        <span className="deal-panel-convo-time">
                          {new Date(c.updated_at || c.created_at).toLocaleDateString()}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Timeline */}
              {timeline.length > 0 && (
                <div className="deal-panel-section">
                  <div className="deal-panel-section-title">Timeline</div>
                  <div className="deal-panel-timeline">
                    {timeline.map((t, i) => (
                      <div key={i} className="deal-panel-timeline-item">
                        <span className="deal-panel-timeline-dot" />
                        <span className="deal-panel-timeline-label">{t.label}</span>
                        <span className="deal-panel-timeline-date">
                          {new Date(t.date).toLocaleDateString()} {new Date(t.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function renderMetric(label: string, value: string) {
  return (
    <div className="deal-panel-metric">
      <span className="deal-panel-metric-label">{label}</span>
      <span className="deal-panel-metric-value">{value}</span>
    </div>
  );
}

function formatCurrency(n: number | undefined | null): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}
